/** URL paths for Knowledge (shared shape on one.atw.net and agents.atw.net). */

const BASE = "/knowledge";

export function knowledgeOverviewPath(): string {
  return `${BASE}/bases`;
}

export function knowledgeBasePath(baseId: string): string {
  return `${BASE}/bases/${encodeURIComponent(baseId.trim())}`;
}

export function knowledgeLibrariesPath(baseId: string): string {
  return `${knowledgeBasePath(baseId)}/libraries`;
}

export function knowledgeLibraryPath(baseId: string, libraryId: string): string {
  return `${knowledgeLibrariesPath(baseId)}/${encodeURIComponent(libraryId.trim())}`;
}

export function knowledgeBooksPath(baseId: string): string {
  return `${knowledgeBasePath(baseId)}/books`;
}

export function knowledgeBookPath(baseId: string, bookKey: string): string {
  return `${knowledgeBooksPath(baseId)}/${encodeURIComponent(bookKey.trim())}`;
}

export function knowledgeArticlesPath(baseId: string): string {
  return `${knowledgeBasePath(baseId)}/articles`;
}

export function knowledgeArticleNewPath(baseId: string): string {
  return `${knowledgeArticlesPath(baseId)}/new`;
}

export function knowledgeArticlePath(baseId: string, articleId: string): string {
  return `${knowledgeArticlesPath(baseId)}/${encodeURIComponent(articleId.trim())}`;
}

export function knowledgeArticleEditPath(baseId: string, articleId: string): string {
  return `${knowledgeArticlePath(baseId, articleId)}/edit`;
}

export type KnowledgeRouteSection = "libraries" | "books" | "articles";

const BASED_PATH =
  /^\/knowledge\/bases\/([^/]+)(?:\/(libraries|books|articles)(?:\/([^/]+)(?:\/edit)?)?)?\/?$/;

/** Decode the knowledge base id from a path, when present. */
export function parseKnowledgeBaseIdFromPath(pathname: string): string | null {
  const match = pathname.match(BASED_PATH);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]).trim() || null;
  } catch {
    return match[1].trim() || null;
  }
}

/** Rebuild the same section under a different knowledge base (for base switcher). */
export function knowledgePathAfterBaseSwitch(pathname: string, nextBaseId: string): string | null {
  const match = pathname.match(BASED_PATH);
  if (!match) return null;
  const section = match[2] as KnowledgeRouteSection | undefined;
  const tail = match[3];
  const base = nextBaseId.trim();
  if (!base) return null;
  if (!section) return knowledgeBasePath(base);
  if (section === "libraries") {
    return tail ? knowledgeLibraryPath(base, tail) : knowledgeLibrariesPath(base);
  }
  if (section === "books") {
    return tail ? knowledgeBookPath(base, tail) : knowledgeBooksPath(base);
  }
  if (section === "articles") {
    if (!tail) return knowledgeArticlesPath(base);
    if (pathname.endsWith("/edit")) return knowledgeArticleEditPath(base, tail);
    return knowledgeArticlePath(base, tail);
  }
  return null;
}

/** Map legacy flat paths to a section name for redirects. */
export function legacyKnowledgeSection(pathname: string): KnowledgeRouteSection | "bases" | null {
  if (pathname === "/knowledge" || pathname === "/knowledge/") return "bases";
  if (pathname.startsWith("/knowledge/libraries")) return "libraries";
  if (pathname.startsWith("/knowledge/books")) return "books";
  if (pathname.startsWith("/knowledge/articles")) return "articles";
  return null;
}

export function legacyKnowledgeTail(pathname: string): string | null {
  const lib = pathname.match(/^\/knowledge\/libraries\/([^/]+)/);
  if (lib) return decodeURIComponent(lib[1]);
  const book = pathname.match(/^\/knowledge\/books\/([^/]+)/);
  if (book) return decodeURIComponent(book[1]);
  const articleEdit = pathname.match(/^\/knowledge\/articles\/([^/]+)\/edit/);
  if (articleEdit) return decodeURIComponent(articleEdit[1]);
  const article = pathname.match(/^\/knowledge\/articles\/([^/]+)/);
  if (article && article[1] !== "new") return decodeURIComponent(article[1]);
  return null;
}

export function legacyKnowledgeIsArticleNew(pathname: string): boolean {
  return /^\/knowledge\/articles\/new\/?$/.test(pathname);
}

export function legacyKnowledgeIsArticleEdit(pathname: string): boolean {
  return /^\/knowledge\/articles\/[^/]+\/edit\/?$/.test(pathname);
}

function normalizeKnowledgePath(pathname: string): string {
  const raw = pathname.split("?")[0] || "/";
  if (raw.length > 1 && raw.endsWith("/")) return raw.slice(0, -1);
  return raw;
}

/** Sidebar active state for Knowledge module links (legacy and `/knowledge/bases/:id/...`). */
export function knowledgeModuleNavActive(pathname: string, href: string): boolean {
  const p = normalizeKnowledgePath(pathname);
  const target = normalizeKnowledgePath(href.split("?")[0] || href);

  if (target === "/knowledge") {
    if (p === "/knowledge" || p === "/knowledge/bases") return true;
    if (/^\/knowledge\/bases\/[^/]+$/.test(p)) return true;
    return false;
  }
  if (target === "/knowledge/libraries") {
    return p.includes("/libraries");
  }
  if (target === "/knowledge/books") {
    return p.startsWith("/knowledge") && /(?:^|\/)books(?:\/|$)/.test(p);
  }
  if (target === "/knowledge/articles") {
    return p.startsWith("/knowledge") && /(?:^|\/)articles(?:\/|$)/.test(p);
  }
  return false;
}
